import { useRef, useState } from "react";
import "./ChangePassword.scss";
import { Modal, Form, Typography, Input, message } from "antd";
import changePasswordSchema from "../../validations/changePasswordValidation";
import { createYupValidator } from "../../utils/validationHelper";
import { changePassword } from "../../features/forgetPassword/resetPasswordActions";
import { logOut } from "../../features/auth/authSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { staticTextErrors } from "../../config/constantErrorTexts";

export default function ChangePasswordModal({
  token,
  isOpened,
  title,
  onCancel,
}) {
  const formRef = useRef(null);
  const [messageApi, contextHolder] = message.useMessage();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const KNOWN_FIELDS = [
    "current_password",
    "new_password",
    "new_password_confirm",
  ];

  const openMessage = (type, text) => {
    messageApi.open({
      type: type,
      content: text,
    });
  };

  const resetModalFields = () => {
    formRef?.current?.resetFields();
    setLoading(false);
    if (error) {
      setError(null);
    }
  };

  const handleCancelModal = () => {
    onCancel();
    resetModalFields();
  };

  const onOk = (values) => {
    setLoading(true);
    dispatch(changePassword(values))
      ?.then((res) => {
        if (res.type === "change_password/fulfilled") {
          openMessage(
            "success",
            res?.payload?.message
              ? res?.payload?.message
              : "Password changed successfully"
          );
          setTimeout(() => {
            handleCancelModal();
            dispatch(logOut());
            navigate("/login");
          }, 1500);
        } else if (res.type === "change_password/rejected") {
          if (res?.payload?.current_password) {
            formRef?.current?.setFields([
              {
                name: "current_password",
                errors: Array.isArray(res?.payload?.current_password)
                  ? res?.payload?.current_password
                  : [res?.payload?.current_password],
              },
            ]);
          } else if (res?.payload?.new_password) {
            formRef?.current?.setFields([
              {
                name: "new_password",
                errors: Array.isArray(res?.payload?.new_password)
                  ? res?.payload?.new_password
                  : [res?.payload?.new_password],
              },
            ]);
          } else if (res?.payload?.new_password_confirm) {
            formRef?.current?.setFields([
              {
                name: "new_password_confirm",
                errors: Array.isArray(res?.payload?.new_password_confirm)
                  ? res?.payload?.new_password_confirm
                  : [res?.payload?.new_password_confirm],
              },
            ]);
          } else {
            openMessage(
              "error",
              res.payload && typeof res.payload === "string"
                ? res.payload
                : staticTextErrors?.SERVER_ERROR
            );
          }
          const fieldErrors =
            res?.payload && typeof res?.payload === "object"
              ? Object.fromEntries(
                  Object.entries(res.payload).filter(([key]) =>
                    KNOWN_FIELDS.includes(key)
                  )
                )
              : null;
          setError(
            fieldErrors && Object.keys(fieldErrors).length ? fieldErrors : null
          );
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
        setError(null);
        openMessage("error", staticTextErrors?.SERVER_ERROR);
      });
  };

  return (
    <>
      {contextHolder}
      <Modal
        className="changePasswordModal-cont"
        style={{
          borderTop: `2px solid ${token?.colorPrimary}`,
        }}
        open={isOpened}
        title={title}
        centered={true}
        okText="Save"
        closeIcon={false}
        destroyOnHidden={true}
        onOk={() => {
          formRef?.current?.submit();
        }}
        onCancel={() => {
          handleCancelModal();
        }}
        okButtonProps={{
          htmlType: "submit",
          type: "primary",
          key: "submit",
          loading: loading,
          disabled: error ? Object.keys(error)?.length !== 0 : false,
        }}>
        <Form
          className="changePasswordModal-cont__form"
          ref={formRef}
          layout="vertical"
          onFinish={(values) => {
            onOk(values);
          }}>
          <Form.Item
            className="changePasswordModal-cont__form__formItem"
            name="current_password"
            rules={[
              {
                required: true,
                validator: createYupValidator(
                  changePasswordSchema,
                  "current_password"
                ),
              },
            ]}
            label={<Typography.Text>Current Password</Typography.Text>}
            colon={false}>
            <Input.Password
              type="text"
              placeholder="Current Password"
              onChange={() => {
                if (error && error["current_password"]) {
                  const { current_password, ...rest } = error;
                  setError(Object.keys(rest).length ? rest : null);
                }
              }}
            />
          </Form.Item>
          <Form.Item
            className="changePasswordModal-cont__form__formItem"
            name="new_password"
            dependencies={["current_password"]}
            rules={[
              {
                required: true,
                validator: async (_, value) => {
                  try {
                    const formValues = formRef.current?.getFieldsValue();
                    await changePasswordSchema?.validateAt(
                      "new_password",
                      formValues
                    );
                    return Promise.resolve();
                  } catch (error) {
                    return Promise.reject(error.message);
                  }
                },
              },
            ]}
            hasFeedback
            label={<Typography.Text>New Password</Typography.Text>}
            colon={false}>
            <Input.Password
              type="text"
              placeholder="New Password"
              onChange={() => {
                if (error && error["new_password"]) {
                  const { new_password, ...rest } = error;
                  setError(Object.keys(rest).length ? rest : null);
                }
              }}
            />
          </Form.Item>
          <Form.Item
            className="changePasswordModal-cont__form__formItem"
            name="new_password_confirm"
            dependencies={["new_password"]}
            rules={[
              {
                required: true,
                validator: async (_, value) => {
                  try {
                    const formValues = formRef.current?.getFieldsValue();
                    await changePasswordSchema?.validateAt(
                      "new_password_confirm",
                      formValues
                    );
                    return Promise.resolve();
                  } catch (error) {
                    return Promise.reject(error.message);
                  }
                },
              },
            ]}
            hasFeedback
            label={<Typography.Text>Repeat New Password</Typography.Text>}
            colon={false}>
            <Input.Password
              type="text"
              placeholder="Repeat New Password"
              onChange={() => {
                if (error && error["new_password_confirm"]) {
                  const { new_password_confirm, ...rest } = error;
                  setError(Object.keys(rest).length ? rest : null);
                }
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
